import { format, differenceInDays } from 'date-fns';
import { isNil, padStart } from 'lodash';

export const getTodaysDate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

export const getYesterdaysDate = () => {
  const today = getTodaysDate();
  today.setDate(today.getDate() - 1);
  return today;
};

export const getTimeInputFormat = (val) => {
  if (val === null || val === undefined) {
    return val;
  }

  return format(new Date(val), 'HH:mm');
};

export const getDateInputFormat = (val) => {
  if (val === null || val === undefined) {
    return val;
  }

  return format(new Date(val), 'YYYY-MM-DD');
};

export const getDateTimeInputFormat = (val) => {
  if (val === null || val === undefined) {
    return val;
  }

  return format(new Date(val), 'YYYY-MM-DDTHH:mm');
};

export const getUTCDateTime = (val) => {
  if (val === null || val === undefined) {
    return val;
  }

  return new Date(val).toISOString();
};

export const getClientDateWithFormat = (val, formatString, timeZone = 'America/Chicago') => {
  if (isNil(val)) {
    return format(new Date().toLocaleString('en-US', { timeZone }), formatString);
  }

  if (typeof val === 'string' && val[val.length - 1].toLowerCase() !== 'z') {
    return format(new Date(`${val }Z`).toLocaleString('en-US', { timeZone }), formatString);
  }

  return format(new Date(val).toLocaleString(), formatString);
};

export const getClientDateTime = (val, ignoreSeconds, timeZone = 'America/Chicago') => {
  const formatString = `MM/DD/YYYY h:mm${ignoreSeconds ? '' : ':ss'} A`;
  return getClientDateWithFormat(val, formatString, timeZone);
};

export const getClientShortDateTime = (val, ignoreSeconds, timeZone = 'America/Chicago') => {
  const formatString = `MM/DD h:mm${ignoreSeconds ? '' : ':ss'} A`;
  return getClientDateWithFormat(val, formatString, timeZone);
};

export const getTenureFromNumberOfDays = (days, showDays = true) => {
  let months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  days -= 30 * months;
  months -= 12 * years;

  const getDatePart = (amount, partAsString) => {
    let part = '';

    if (amount > 0) {
      part += `${amount} ${partAsString}`;

      if (amount > 1) {
        part += 's';
      }

      part += ' ';
    }

    return part;
  };

  let result;

  if (showDays) {
    result = getDatePart(years, 'yr') + getDatePart(months, 'mth') + getDatePart(days, 'day');
  } else if (!years && !months) {
    result = getDatePart(days, 'day');
  } else {
    result = getDatePart(years, 'year') + getDatePart(months, 'month');
  }

  return result.trim();
};

export const getTenure = (fromDate, toDate) => {
  if (fromDate === null || fromDate === undefined) {
    return null;
  }

  const today = new Date(toDate ?? new Date().toUTCString());

  if (new Date(fromDate) > today) {
    return 'Invalid dates';
  }

  const timeDiff = new Date(today - new Date(fromDate));

  const day = 1000 * 60 * 60 * 24;

  const days = Math.floor(timeDiff / day);
  return getTenureFromNumberOfDays(days);
};

export const convertDateToPicker = (date) => {
  if (!date) {
    return null;
  }

  const [month, day, year] = date.split('/');
  if (day && year) {
    return `${year}-${month}-${day}`;
  }
  return date;
};

export const formatDateForField = (val) => {
  if (val === null || val === undefined) {
    return val;
  }

  const date = val.substr(0, 10);
  const [year, month, day] = date.split('-');
  if (month && day) {
    return `${month}/${day}/${year}`;
  }
  return date;
};

export const formatTimeForField = (val) => {
  if (val === null || val === undefined) {
    return val;
  }

  return val.substr(11, 5);
};

/**
 * convert date ISO-like string to Date object
 *
 *  @param {string} utcString - The date in ISO-like format
 *  @param {Boolean?} ignoreTimezone - If true, timezone info is removed
 *  @returns {Date | null} - The date object or null
 * */
export const getDateFromString = (utcString, ignoreTimezone = false) => {
  if (!utcString || utcString === '') {
    return null;
  }

  // if DateOnly - lets add 0 time to it
  if (utcString.length === 10 && utcString.includes('-') && !utcString.includes('T')) {
    utcString += 'T00:00:00';
  }

  if (!ignoreTimezone) {
    const timezoned = utcString.endsWith('Z') ? utcString : `${utcString}Z`;
    return new Date(timezoned);
  }

  const cleared = utcString.endsWith('Z') ? utcString.replace('Z', '') : utcString;

  return new Date(cleared);
};

/**
 * Format date
 *
 *  @param {string | Date | null} date - The datetime to format
 *  @param {Boolean?} ignoreTimezone - If false, date is considered to be as UTC (True by default)
 *  @returns {string | null} - Formatted string or null
 * */
export const formatDate = (date, ignoreTimezone = true) => {
  const dateObj = date instanceof Date ? date : getDateFromString(date, ignoreTimezone);
  if (!dateObj) {
    return null;
  }
  return format(dateObj, 'MM/DD/YYYY');
};

/**
 * Format time - Returns only the HH:mm for a given date
 *
 *  @param {string | Date | null} date - The datetime to format
 *  @param {Boolean?} ignoreTimezone - If false, date is considered to be as UTC (True by default)
 *  @returns {string | null} - Formatted string or null
 * */
export const formatTime = (date, ignoreTimezone = true) => {
  const dateObj = date instanceof Date ? date : getDateFromString(date, ignoreTimezone);
  if (!dateObj) {
    return null;
  }
  return format(dateObj, 'HH:mm');
};

const defaultFormatConfig = {
  ignoreSeconds: true,
  ignoreTimezone: false,
};

/**
 * Get Utc Now date
 * @returns {Date} - The current date in UTC
 * */
export const getUtcNow = () => {
  const now = new Date();
  return new Date(now.getTime() + now.getTimezoneOffset() * 60000);
};

/**
 * Format dateTime
 *
 *  @param {string | Date | null} dateTime - The datetime to format
 *  @param {Object?} config
 *  @param {Boolean?} config.ignoreSeconds
 *  @param {Boolean?} config.ignoreTimezone
 *  @returns {string | null} - Formatted string or null
 * */
export const formatDateTime = (dateTime, config = defaultFormatConfig) => {
  const { ignoreSeconds, ignoreTimezone } = { ...defaultFormatConfig, ...config };

  const dateTimeObj = dateTime instanceof Date ? dateTime : getDateFromString(dateTime, ignoreTimezone);

  if (!dateTimeObj) {
    return null;
  }

  const formatString = `MM/DD/YYYY HH:mm${ignoreSeconds ? '' : ':ss'}`;
  return format(dateTimeObj, formatString);
};

/**
 * Format date object to YYYY-MM-DDT00:00:00
 *
 *  @param {Date | null} date - The datetime to format
 *  @param {Boolean?} formatStringAsDateOnly - If true, time part is removed
 *  @returns {string | null} - Formatted string or null
 */

export const getDateStringForRequest = (date, formatStringAsDateOnly = false) => {
  if (!date) {
    return null;
  }

  if (!(date instanceof Date)) {
    return formatStringAsDateOnly ? date.split('T')[0] : date;
  }

  // eslint-disable-next-line max-len
  return `${date.getFullYear()}-${padStart((date.getMonth() + 1).toString(), 2, '0')}-${padStart(date.getDate().toString(), 2, '0')}${formatStringAsDateOnly ? '' : 'T00:00:00'}`;
};

/**
 * Format datetime object to YYYY-MM-DDTHH:mm:ss
 *
 *  @param {Date | null} date - The datetime to format
 *  @returns {string | null} - Formatted string or null
 */

export const getDateTimeStringForRequest = (date) => {
  if (!date) {
    return null;
  }

  if (!(date instanceof Date)) {
    return date;
  }

  return `${format(date, 'YYYY-MM-DDTHH:mm:ss')}.000`;
};

/**
 * convert date codes to formatted date for ease of user entry
 *
 *  @param {string} shortCut - The shortcut code for a date (eg. 10 is 10 days from now, 101 is Jan 1st, 1001 is Oct 1st)
 *  @param {Date?} refDate - Day to reason range from
 *  @returns {string} - The date in useful format (MM/DD/YYYY) or empty if not parsable as a shortcut
 * */
export const dateShortcutToDateString = (shortCut, refDate = new Date()) => {
  const date = new Date(refDate);
  const shortcutNum = Number.parseInt(shortCut);
  if (Math.abs(shortcutNum) < 100) {
    date.setDate(date.getDate() + Number.parseInt(shortCut));
  } else if (Math.abs(shortcutNum) < 10_000) {
    const day = Math.abs(shortcutNum) % 100;
    date.setDate(day);
    const month = Number.parseInt(Math.abs(shortcutNum) / 100) - 1;
    date.setMonth(month);
    // give shortcuts for last and next year
    const year = date.getFullYear() - (shortCut[0] === '-' ? 1 : shortCut[0] === '+' ? -1 : 0);
    date.setFullYear(year);
  } else { return ''; }
  const formated = formatDate(date.toISOString());
  return formated;
};
export const getDateIsoStringIgnoringTimezone = (date) => {
  if (!date) {
    return null;
  }

  // convert to nearly the iso format
  const fakeIsoString = format(new Date(date), 'YYYY-MM-DDTHH:mm:ss');
  return `${fakeIsoString}`;
};

// Validation handlers

/**
 * Check if date is in the future
 *
 *  @param {string | Date | null} date - The date to compare with today
 *  @returns {boolean} - True if date is in the future, false otherwize
 * */
export const isFuture = (date) => {
  if (!date) {
    return false;
  }

  if (date instanceof Date) {
    return date > new Date();
  }

  return date.substring(0, 10) > format(new Date(), 'YYYY-MM-DD');
};

/**
 * Check if date is not in the future (past or today)
 *
 *  @param {string | Date | null} date - The date to compare with today
 *  @returns {boolean} - True if date is not in the future, false otherwize
 * */
export const isNotFuture = (date) => {
  if (!date) {
    return true;
  }

  if (date instanceof Date) {
    return date <= new Date();
  }

  return date.substring(0, 10) <= format(new Date(), 'YYYY-MM-DD');
};

/**
 * Check if date is in the past
 *
 *  @param {string | Date | null} date - The date to compare with today
 *  @returns {boolean} - True if date is in the past, false otherwize
 * */
export const isPast = (date) => {
  if (!date) {
    return false;
  }

  if (date instanceof Date) {
    return date < new Date();
  }

  return date.substring(0, 10) < format(new Date(), 'YYYY-MM-DD');
};

/**
 * Check if date is not in the past (future or today)
 *
 *  @param {string | Date | null} date - The date to compare with today
 *  @returns {boolean} - True if date is not in the past, false otherwize
 * */
export const isNotPast = (date) => {
  if (!date) {
    return true;
  }

  if (date instanceof Date) {
    return date >= new Date();
  }

  return date.substring(0, 10) >= format(new Date(), 'YYYY-MM-DD');
};

/**
 * Calculate the difference in business days between two dates (compatible with date-fns v1)
 * Excludes weekends (Saturday and Sunday)
 *
 *  @param {Date} endDate - The end date (later date)
 *  @param {Date} startDate - The start date (earlier date)
 *  @returns {number} - The difference in business days
 * */
const calculateBusinessDays = (endDate, startDate) => {
  if (endDate < startDate) {
    return -calculateBusinessDays(startDate, endDate);
  }
  
  let businessDays = 0;
  const current = new Date(startDate);
  
  while (current < endDate) {
    const dayOfWeek = current.getDay();
    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      businessDays++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return businessDays;
};

/**
 * Calculate the difference between two dates in either business days or calendar days
 *
 *  @param {string | Date} endDate - The end date (later date)
 *  @param {string | Date | null} startDate - The start date (earlier date). Defaults to today if null
 *  @param {boolean} useBusinessDays - If true, calculates business days difference; if false, calendar days
 *  @returns {number} - The difference in days (positive if endDate is after startDate, negative if before)
 * */
export const getDateDifference = (endDate, startDate = null, useBusinessDays = false) => {
  if (!endDate) return 0;
  
  const end = new Date(endDate);
  const start = startDate ? new Date(startDate) : new Date();
  
  // Set both to start of day for consistent comparison
  end.setHours(0, 0, 0, 0);
  start.setHours(0, 0, 0, 0);
  
  if (useBusinessDays) {
    return calculateBusinessDays(end, start);
  } else {
    return differenceInDays(end, start);
  }
};
